/**
 * Vendored SHA-1 so the package has zero runtime dependencies — it backs UUIDv5
 * (RFC 4122). Correctness is pinned by golden tests against the `uuid` npm package.
 */

/** SHA-1 digest bytes of `data`. */
export function sha1(data: Uint8Array): Uint8Array {
    const padded = padMessage(data);
    const dv = new DataView(padded.buffer, padded.byteOffset, padded.byteLength);

    let h0 = 0x67452301, h1 = 0xefcdab89, h2 = 0x98badcfe, h3 = 0x10325476, h4 = 0xc3d2e1f0;
    const w = new Uint32Array(80);
    for (let off = 0; off < padded.length; off += 64) {
        for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
        for (let i = 16; i < 80; i++) {
            const n = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
            w[i] = (n << 1) | (n >>> 31);
        }
        let a = h0, b = h1, c = h2, d = h3, e = h4;
        for (let i = 0; i < 80; i++) {
            let f: number, k: number;
            if (i < 20) { f = (b & c) | (~b & d); k = 0x5a827999; }
            else if (i < 40) { f = b ^ c ^ d; k = 0x6ed9eba1; }
            else if (i < 60) { f = (b & c) | (b & d) | (c & d); k = 0x8f1bbcdc; }
            else { f = b ^ c ^ d; k = 0xca62c1d6; }
            const t = ((((a << 5) | (a >>> 27)) + f + e + k + w[i]) | 0);
            e = d; d = c; c = (b << 30) | (b >>> 2); b = a; a = t;
        }
        h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0; h4 = (h4 + e) | 0;
    }

    const out = new Uint8Array(20);
    const odv = new DataView(out.buffer);
    odv.setUint32(0, h0 >>> 0);
    odv.setUint32(4, h1 >>> 0);
    odv.setUint32(8, h2 >>> 0);
    odv.setUint32(12, h3 >>> 0);
    odv.setUint32(16, h4 >>> 0);
    return out;
}

export function toHex(bytes: Uint8Array): string {
    let s = "";
    for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, "0");
    return s;
}

/** append 0x80, zero-pad to 56 mod 64, append big-endian 64-bit bit-length */
function padMessage(data: Uint8Array): Uint8Array {
    const bitLen = data.length * 8;
    const padded = new Uint8Array((((data.length + 8) >> 6) + 1) * 64);
    padded.set(data);
    padded[data.length] = 0x80;
    const dv = new DataView(padded.buffer);
    dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
    dv.setUint32(padded.length - 4, bitLen >>> 0);
    return padded;
}
